import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Building2 } from "lucide-react";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiErrorDetail } from "@/lib/api";
import { CATEGORIES, PRODUCTS } from "@/data/storeProducts";

const initialForm = {
  full_name: "",
  email: "",
  phone: "",
  customer_type: "",
  product_of_interest: "",
  number_of_units: "",
  message: "",
};

export default function Store() {
  const [activeCategory, setActiveCategory] = useState("all");
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);

  const filtered = useMemo(
    () => (activeCategory === "all" ? PRODUCTS : PRODUCTS.filter((p) => p.category === activeCategory)),
    [activeCategory]
  );

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  async function submitEnquiry(e) {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.customer_type) {
      toast.error("Please fill in name, email and customer type.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        number_of_units: form.number_of_units ? parseInt(form.number_of_units, 10) : null,
        phone: form.phone || null,
        product_of_interest: form.product_of_interest || null,
        message: form.message || null,
      };
      await api.post("/store/enquiry", payload);
      toast.success("Enquiry received. Our team will be in touch shortly.");
      setForm(initialForm);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="bg-bone min-h-screen">
      <Navbar />

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-16 md:pt-24 md:pb-24">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-8 animate-fade-up">
            <p className="label-eyebrow mb-6" data-testid="store-eyebrow">NOA Store</p>
            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl font-light tracking-tighter leading-[0.95] text-forest">
              Smart devices designed for a<br />
              <em className="not-italic text-terracotta">more efficient</em> home.
            </h1>
            <p className="mt-8 text-lg md:text-xl text-ink/70 max-w-2xl leading-relaxed">
              Connect compatible smart devices with NOA to monitor usage, automate your home, prevent waste, and receive intelligent sustainability insights.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Button
                onClick={() => scrollTo("products")}
                className="bg-forest text-bone hover:bg-forest-hover rounded-full px-8 py-6 text-base font-medium"
                data-testid="store-cta-explore"
              >
                Explore Devices <ArrowRight className="ml-2" size={18} />
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setActiveCategory("kits");
                  scrollTo("products");
                }}
                className="rounded-full border-forest text-forest hover:bg-forest hover:text-bone px-8 py-6 text-base"
                data-testid="store-cta-kits"
              >
                View Starter Kits
              </Button>
            </div>
          </div>

          <div className="md:col-span-4 animate-fade-up" style={{ animationDelay: "120ms" }}>
            <div className="surface-card p-8">
              <p className="label-eyebrow">What to expect</p>
              <ul className="mt-4 space-y-3 text-sm text-ink/70 leading-relaxed">
                <li className="flex items-start gap-3">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-forest shrink-0" />
                  Every device pairs to the NOA app in the same guided flow.
                </li>
                <li className="flex items-start gap-3">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-forest shrink-0" />
                  No hub required for the core catalog.
                </li>
                <li className="flex items-start gap-3">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-forest shrink-0" />
                  Devices ship as they're released — join the wait list to be notified.
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Category filter */}
      <section id="products" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <p className="label-eyebrow mb-2">Catalog</p>
            <h2 className="font-serif text-3xl md:text-4xl text-forest font-light">Browse by category</h2>
          </div>
          <div className="flex flex-wrap gap-2" data-testid="category-filter">
            {CATEGORIES.map((c) => (
              <button
                key={c.id}
                onClick={() => setActiveCategory(c.id)}
                className={`px-4 py-2 rounded-full text-sm border transition-colors ${
                  activeCategory === c.id
                    ? "bg-forest text-bone border-forest"
                    : "bg-white text-ink/70 border-[#E2DFD8] hover:border-forest hover:text-forest"
                }`}
                data-testid={`category-${c.id}`}
              >
                {c.label}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="product-grid">
          {filtered.map((p) => (
            <ProductCard key={p.slug} p={p} />
          ))}
        </div>
      </section>

      {/* Property Manager Hardware Packages */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-32">
        <div className="relative rounded-3xl bg-forest text-bone p-10 md:p-14 overflow-hidden" data-testid="property-package-cta">
          <div className="absolute -right-16 -top-16 w-72 h-72 rounded-full bg-terracotta/10 blur-3xl" />
          <div className="relative grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
            <div className="md:col-span-8">
              <p className="label-eyebrow text-bone/60 mb-4">Property Manager Hardware Packages</p>
              <h2 className="font-serif text-4xl md:text-5xl font-light tracking-tight leading-[1.05]">
                Planning smart monitoring across multiple units or properties?
              </h2>
              <p className="mt-6 text-lg text-bone/70 max-w-2xl leading-relaxed">
                Contact NOA for compatible device packages and deployment support.
              </p>
            </div>
            <div className="md:col-span-4 flex md:justify-end">
              <Button
                onClick={() => {
                  setForm((f) => ({ ...f, customer_type: "property_manager" }));
                  scrollTo("enquiry");
                }}
                className="rounded-full bg-bone text-forest hover:bg-bone/90 px-8 py-6 text-base font-medium"
                data-testid="request-property-package"
              >
                <Building2 size={18} className="mr-2" /> Request Property Package
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Enquiry form */}
      <section id="enquiry" className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mt-24">
        <div className="surface-card p-8 md:p-12">
          <p className="label-eyebrow mb-4">Enquiry</p>
          <h2 className="font-serif text-4xl md:text-5xl font-light text-forest tracking-tight">Get in touch.</h2>
          <p className="mt-3 text-ink/65 max-w-xl">
            Tell us about your home or property. We'll follow up with availability, compatibility guidance, and next steps.
          </p>

          <form onSubmit={submitEnquiry} className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="enquiry-form">
            <Input
              placeholder="Full name"
              required
              value={form.full_name}
              onChange={(e) => setForm({ ...form, full_name: e.target.value })}
              className="rounded-xl border-[#E2DFD8] bg-white h-12"
              data-testid="enquiry-full-name"
            />
            <Input
              type="email"
              placeholder="Email address"
              required
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="rounded-xl border-[#E2DFD8] bg-white h-12"
              data-testid="enquiry-email"
            />
            <Input
              placeholder="Phone number"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              className="rounded-xl border-[#E2DFD8] bg-white h-12"
              data-testid="enquiry-phone"
            />
            <Select value={form.customer_type} onValueChange={(v) => setForm({ ...form, customer_type: v })}>
              <SelectTrigger className="rounded-xl border-[#E2DFD8] bg-white h-12" data-testid="enquiry-customer-type">
                <SelectValue placeholder="Customer type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="home_user">Home User</SelectItem>
                <SelectItem value="property_manager">Property Manager</SelectItem>
              </SelectContent>
            </Select>
            <Select value={form.product_of_interest} onValueChange={(v) => setForm({ ...form, product_of_interest: v })}>
              <SelectTrigger className="rounded-xl border-[#E2DFD8] bg-white h-12" data-testid="enquiry-product">
                <SelectValue placeholder="Product of interest (optional)" />
              </SelectTrigger>
              <SelectContent>
                {PRODUCTS.map((p) => (
                  <SelectItem key={p.slug} value={p.slug}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              type="number"
              min="1"
              placeholder="Number of units (optional)"
              value={form.number_of_units}
              onChange={(e) => setForm({ ...form, number_of_units: e.target.value })}
              className="rounded-xl border-[#E2DFD8] bg-white h-12"
              data-testid="enquiry-units"
            />
            <Textarea
              placeholder="Message"
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              className="rounded-xl border-[#E2DFD8] bg-white md:col-span-2 min-h-28"
              data-testid="enquiry-message"
            />
            <Button
              type="submit"
              disabled={submitting}
              className="bg-forest hover:bg-forest-hover text-bone rounded-full px-8 py-6 md:col-span-2 mt-2 disabled:opacity-60"
              data-testid="enquiry-submit"
            >
              {submitting ? "Submitting…" : "Submit Enquiry"}
            </Button>
          </form>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function ProductCard({ p }) {
  const Icon = p.icon;
  return (
    <Link
      to={`/store/${p.slug}`}
      className="group flex flex-col rounded-3xl bg-white border border-[#E2DFD8] overflow-hidden transition-all hover:-translate-y-1 hover:shadow-xl"
      data-testid={`product-card-${p.slug}`}
    >
      {/* Image placeholder */}
      <div className={`aspect-[4/3] bg-gradient-to-br ${p.tint} relative flex items-center justify-center border-b border-[#E2DFD8]`}>
        <Icon size={56} strokeWidth={1.2} className="text-forest/70" />
        <span className="absolute top-3 right-3 text-[10px] uppercase tracking-widest bg-bone/95 text-forest px-2.5 py-1 rounded-full font-mono border border-[#E2DFD8]">
          Coming Soon
        </span>
      </div>
      <div className="p-6 flex flex-col flex-1">
        <p className="label-eyebrow">{p.categoryLabel}</p>
        <h3 className="font-serif text-2xl text-ink mt-2 leading-tight">{p.name}</h3>
        <p className="mt-2 text-sm text-ink/65 leading-relaxed flex-1">{p.short}</p>
        <p className="mt-4 text-[11px] font-mono text-ink/50">{p.compatibility}</p>
        <div className="mt-5">
          <span
            className="inline-flex items-center gap-1.5 text-sm font-medium text-forest group-hover:text-forest-hover"
            data-testid={`product-view-${p.slug}`}
          >
            View Details <ArrowRight size={14} className="transition-transform group-hover:translate-x-0.5" />
          </span>
        </div>
      </div>
    </Link>
  );
}
